/* Tasks — STORY-025 and STORY-026.
 *
 * ── the assignee is an id, and it is required ───────────────────────────────
 * Never a name, and never absent. A text assignee is what makes a spreadsheet's
 * task sheet unfilterable; an optional one buys an "unassigned" bucket at the
 * price of every Task being allowed to belong to nobody, which EPIC-005
 * rejected on 2026-08-14.
 *
 * ── a date is a date or it is nothing ───────────────────────────────────────
 * Parsed as well as shape-matched, exactly as events and transactions are and
 * for the reason those files give at length: the shape alone accepts
 * `2026-02-31`, which reaches the `date` column and makes Postgres raise — an
 * unexpected throw that the error boundary turns into a 500, where a client's
 * malformed input has to be a 400.
 *
 * ── the far-future date warns; it does not refuse ───────────────────────────
 * A mistyped year is caught by asking, because a genuinely distant date is a
 * real thing to want. The confirmation travels as a flag on the request rather
 * than as a second endpoint, so the refusal and the acceptance are one code
 * path with one set of rules.
 */
import { TASK_PRIORITIES, TASK_STATUSES, } from '@eventuley/domain';
import * as z from 'zod';
export { TASK_PRIORITIES, TASK_STATUSES };
/** Long enough for a real job, short enough to render in a list row. */
export const TASK_DESCRIPTION_MAX = 500;
const description = z.string().trim().min(1, 'A description is needed.').max(TASK_DESCRIPTION_MAX);
/**
 * A calendar date, round-tripped rather than fed to `new Date`.
 *
 * `new Date('2026-02-31')` rolls forward to 3 March rather than refusing, so
 * the parse has to be checked against what it produced.
 */
const taskDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date, as YYYY-MM-DD.')
    .refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined)
        return false;
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day);
}, 'That is not a date on any calendar.');
export const taskAssigneeSchema = z.object({
    userId: z.string(),
    /** Empty for an erased User — ADR-0015 severs the subject, keeps the fact. */
    displayName: z.string(),
    anonymised: z.boolean(),
    /**
     * False when the assignee has since left the Organisation.
     *
     * STORY-026 requires the list to render such a Task rather than erroring, so
     * the client is told rather than left to infer it from a missing name.
     */
    member: z.boolean(),
});
export const taskSchema = z.object({
    id: z.string(),
    description: z.string(),
    assignee: taskAssigneeSchema,
    /** Null is business work — the majority, and not a missing value. */
    event: z.object({ id: z.string(), name: z.string() }).nullable(),
    startsOn: z.string().nullable(),
    dueOn: z.string().nullable(),
    status: z.enum(TASK_STATUSES),
    priority: z.enum(TASK_PRIORITIES),
    createdAt: z.string(),
    /**
     * For optimistic concurrency on an inline status change — STORY-026 refuses
     * to overwrite a Task that moved underneath the reader, and this is what it
     * compares.
     */
    updatedAt: z.string(),
});
export const createTaskRequestSchema = z
    .object({
    description,
    assigneeId: z.string().uuid(),
    eventId: z.string().uuid().nullish(),
    startsOn: taskDate.nullish(),
    dueOn: taskDate.nullish(),
    priority: z.enum(TASK_PRIORITIES),
    /**
     * The reader has seen the far-future warning and meant it.
     *
     * A flag rather than a second endpoint, so accepting and refusing are one
     * code path — two paths would drift, and the one nobody exercises would be
     * the one missing a rule.
     */
    confirmFarFuture: z.boolean().optional(),
})
    .refine((task) => task.startsOn === undefined ||
    task.startsOn === null ||
    task.dueOn === undefined ||
    task.dueOn === null ||
    task.dueOn >= task.startsOn, { message: 'A Task cannot be due before it starts.', path: ['dueOn'] });
/** Absent leaves a field alone; `null` clears the ones that can be cleared. */
export const updateTaskRequestSchema = z
    .object({
    description: description.optional(),
    assigneeId: z.string().uuid().optional(),
    eventId: z.string().uuid().nullish(),
    startsOn: taskDate.nullish(),
    dueOn: taskDate.nullish(),
    status: z.enum(TASK_STATUSES).optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    confirmFarFuture: z.boolean().optional(),
    /**
     * What the caller believed when they started — STORY-026.
     *
     * The whole concurrency story, and it costs nothing to supply: anybody
     * changing a Task is looking at it and therefore already knows. Without it
     * the second write silently overwrites the first, where the story asks that
     * one wins and the other is told.
     */
    expectedUpdatedAt: z.string().optional(),
})
    .refine((task) => task.startsOn === undefined ||
    task.startsOn === null ||
    task.dueOn === undefined ||
    task.dueOn === null ||
    task.dueOn >= task.startsOn, { message: 'A Task cannot be due before it starts.', path: ['dueOn'] });
export const TASKS_PAGE_SIZE = 25;
export const TASKS_PAGE_MAX = 200;
/**
 * What the list is being asked for — STORY-026.
 *
 * The defaults are the story: **mine, and not finished.** A Member who touches
 * no control gets that view, which is why "show only mine" is not a feature
 * here — it is simply what you already see, and clearing the assignee is how
 * you leave it.
 */
export const listTasksQuerySchema = z.object({
    /** A Member's id, or the literal `all` to clear the default. */
    assigneeId: z.union([z.string().uuid(), z.literal('all')]).optional(),
    status: z.array(z.enum(TASK_STATUSES)).optional(),
    priority: z.array(z.enum(TASK_PRIORITIES)).optional(),
    eventId: z.string().uuid().optional(),
    /**
     * The reader's IANA zone, so the server can compute "overdue" as the reader
     * sees it.
     *
     * Sent by the client because only the client knows where the reader is
     * standing — a server-side "today" would answer differently for two people
     * looking at the same link from two continents, and neither would know why.
     */
    timeZone: z.string().optional(),
    limit: z.number().int().min(1).max(TASKS_PAGE_MAX).optional(),
    offset: z.number().int().min(0).optional(),
});
export const tasksResponseSchema = z.object({
    tasks: z.array(taskSchema),
    /** How many match the filters in total, so a client can page honestly. */
    total: z.number().int(),
    /**
     * Overdue Tasks the active filters are hiding — STORY-026's central cost.
     *
     * Prototyping found it: an overdue High-priority Task belonging to a
     * colleague is invisible on the screen whose entire job is telling you what
     * needs doing. Decided 2026-08-14 — keep the opinionated default and **say
     * the number out loud**. It costs nothing on the common day when it is zero.
     *
     * Counted against what is on screen, so a priority or Event filter triggers
     * it exactly as the assignee filter does.
     */
    hiddenOverdue: z.number().int(),
});
//# sourceMappingURL=tasks.js.map